"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleQuestionMark as HelpCircle, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Wandsworth?",
  answer: "Yes. We supply and erect scaffolding across Wandsworth and surrounding South West London areas for residential, commercial, and industrial projects."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Wandsworth?",
  answer: "We provide independent access scaffolding, temporary roofs, edge protection, chimney stacks, birdcages, and specialist access systems engineered for specific site requirements."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We regularly supply access scaffolds, roof edge protection, and weather-protected temporary roofing to support re-roofing, structural repairs, and solar panel installations."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We work alongside main contractors, developers, roofing contractors, solar installers, facilities managers, and local authorities across London and the South East."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Where narrow passageways, public footpaths, or constrained urban boundaries restrict access, we assess site logistics and design compliant access solutions."
}, {
  id: "item-6",
  question: "How do I request a quote in Wandsworth?",
  answer: "Submit your project scope, drawings, or site address through our request quote form. Our team will review the specifications and arrange a site survey if needed."
}];
export default function FaqWandsworth() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faqwandsworth" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="flex flex-col items-center text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-medium">
            <HelpCircle className="size-3.5 text-primary" />
            Wandsworth Access Queries
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default max-w-2xl">
            Frequently asked questions about Wandsworth scaffolding
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Factual answers about our access solutions, coverage, and working practices across South West London.
          </p>
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="bg-card text-card-foreground rounded-xl border border-border p-6 sm:p-8 shadow-sm mb-12">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <div data-index={i} key={faq.id}>
                <AccordionItem value={faq.id} className="border-border py-1">
                  <AccordionTrigger className="text-left text-base sm:text-lg font-medium text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        <div className="rounded-xl bg-accent text-accent-foreground border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4 text-left">
            <div className="p-2.5 rounded-lg bg-background text-primary border border-border shrink-0">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground font-default">
                Have a specific project in Wandsworth?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Send us your site requirements for a detailed, no-obligation quote.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}